import React, { useMemo, useState, useEffect, useRef } from "react";
import DocumentUploadCard from "../../../components/CustomControls/DocumentUpload/DocumentUploadCard";
import { FaFolder, FaArrowLeft } from "react-icons/fa";
import { ModalCreateCategory } from "./ModalCreateCategory";
import { Add } from "../../../components/Icons/Icons";
import { FormControl } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAuthorization } from "../../../components/Authorization/useAuthorization";
import { attachmentEntityType } from "../../../variables/Enums";

const COLORS = {
  bg: "#f4f7fb",
  card: "#ffffff",
  border: "#e6e9ef",
  primary: "#FFA726",
  secondary: "#2E86DE",
  text: "#2b2f33",
  font: "Roboto, 'Segoe UI', Arial, sans-serif",
};

function isFolder(node) {
  return node && node.type === "folder";
}
function getNodeByPath(root, pathArr) {
  var node = root;
  for (var i = 0; i < pathArr.length; i++) {
    var seg = pathArr[i];
    if (!node || !node.children) return null;
    var next = null;
    for (var j = 0; j < node.children.length; j++) {
      var cand = node.children[j];
      // Solo carpetas: evita coincidir un archivo con el mismo nombre que una carpeta
      if (cand.name === seg && isFolder(cand)) {
        next = cand;
        break;
      }
    }
    node = next;
  }
  return node || root;
}

function normalizePathKey(pathStr) {
  if (pathStr == null) return "";
  return String(pathStr)
    .replace(/\\/g, "/")
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean)
    .join("/");
}

/**
 * Mapeo exacto path → subtitle.
 * El servidor devuelve:
 *  - Filas primarias (path hoja, con attachment) → subtítulo de esa carpeta hoja.
 *  - Filas de prefijo (path corto sin attachment) → subtítulo de carpetas intermedias.
 * NO propagamos hacia arriba para evitar que una carpeta padre muestre
 * el subtítulo de una carpeta hija.
 */
function buildPathToSubtitleMap(attachmentPaths) {
  const map = {};
  (attachmentPaths || []).forEach((p) => {
    const rawPath = p.path ?? p.Path;
    const rawSubtitle = p.subtitle ?? p.Subtitle;
    if (!rawPath) return;
    const sub =
      rawSubtitle != null && String(rawSubtitle).trim() !== ""
        ? String(rawSubtitle).trim()
        : "";
    if (!sub) return;
    const normalizedPath = normalizePathKey(rawPath);
    if (!normalizedPath) return;
    if (!map[normalizedPath]) {
      map[normalizedPath] = sub;
    }
  });
  return map;
}

/** Clave estable del slot (misma categoría + entidad) para no duplicar tarjetas en el árbol. */
function attachmentSlotKeyFromRaw(att) {
  if (!att) return "";
  var id = att.id != null ? att.id : att.Id;
  if (id != null && id !== "") return "id:" + id;
  var cat =
    att.attachmentCategoryId != null
      ? att.attachmentCategoryId
      : att.AttachmentCategoryId;
  var et =
    att.entityTypeId != null ? att.entityTypeId : att.EntityTypeId;
  var eid = att.entityId != null ? att.entityId : att.EntityId;
  return "slot:" + et + "|" + String(eid ?? "") + "|" + cat;
}

function insertPathFolder(rootFolder, pathObj, pathToSubtitleMap) {
  if (!pathObj) return;
  var rawPath = pathObj.path != null ? pathObj.path : pathObj.Path;
  if (rawPath == null || typeof rawPath !== "string") return;

  var attachment =
    pathObj.attachment != null ? pathObj.attachment : pathObj.Attachment;

  var segments = normalizePathKey(rawPath).split("/").filter(Boolean);

  var node = rootFolder;
  for (var i = 0; i < segments.length; i++) {
    var seg = segments[i];
    if (!node.children) node.children = [];

    var folderPath = segments.slice(0, i + 1).join("/");
    var isLast = i === segments.length - 1;
    var child = node.children.find(
      (c) => c.name === seg && c.type === "folder"
    );

    if (!child) {
      child = {
        name: seg,
        type: "folder",
        children: [],
        subtitle: pathToSubtitleMap[folderPath] || null,
      };
      node.children.push(child);
    } else if (pathToSubtitleMap[folderPath]) {
      child.subtitle = pathToSubtitleMap[folderPath];
    }

    if (isLast && attachment) {
      var slotKey = attachmentSlotKeyFromRaw(attachment);
      var dup =
        slotKey &&
        child.children.some(
          (c) =>
            c.type === "file" &&
            attachmentSlotKeyFromRaw(c.attachment) === slotKey
        );
      if (!dup) {
        var attName =
          attachment.attachmentCategoryName ||
          attachment.AttachmentCategoryName ||
          seg;
        var attFn =
          attachment.attachmentFileName || attachment.AttachmentFileName || null;
        var attCatId =
          attachment.attachmentCategoryId != null
            ? attachment.attachmentCategoryId
            : attachment.AttachmentCategoryId;
        var attEt =
          attachment.entityTypeId != null
            ? attachment.entityTypeId
            : attachment.EntityTypeId;
        var attMeta =
          attachment.extendedMetadata != null
            ? attachment.extendedMetadata
            : attachment.ExtendedMetadata;
        var attEid =
          attachment.entityId != null ? attachment.entityId : attachment.EntityId;
        var attPractitionerName =
          attachment.practitionerName != null
            ? attachment.practitionerName
            : attachment.PractitionerName;
        child.children.push({
          name: attName,
          type: "file",
          attachment: {
            attachmentFileName: attFn,
            attachmentCategoryId: attCatId,
            attachmentCategoryName: attName,
            entityTypeId: attEt,
            multipleAttachments: !!(
              attachment.multipleAttachments || attachment.MultipleAttachments
            ),
            id: attachment.id != null ? attachment.id : attachment.Id || null,
            extendedMetadata: attMeta || null,
            systemId: "path-" + (attCatId || seg),
            entityId: attEid,
            practitionerName: attPractitionerName,
          },
        });
      }
    }

    node = child;
  }
}

/**
 * El API devuelve attachmentPaths con filas de prefijo (solo subtítulo) antes que las primarias.
 * insertPathFolder añade hermanos en ese orden, así una carpeta puede quedar después de otra
 * numerada mayor (p. ej. 04 tras 05) o tras editar un subtítulo. Ordenamos por nombre con
 * comparación numérica en cada nivel: carpetas primero, luego archivos.
 */
function sortFilesystemChildren(node) {
  if (!node.children || node.children.length === 0) return;
  const typeRank = (t) => (t === "folder" ? 0 : 1);
  node.children.sort((a, b) => {
    const tr = typeRank(a.type) - typeRank(b.type);
    if (tr !== 0) return tr;
    return String(a.name).localeCompare(String(b.name), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  });
  node.children.forEach(sortFilesystemChildren);
}

function buildPathsTree(attachmentPaths) {
  const pathToSubtitleMap = buildPathToSubtitleMap(attachmentPaths);
  const folder = { name: "root", type: "folder", children: [] };
  (attachmentPaths || []).forEach((p) => {
    insertPathFolder(folder, p, pathToSubtitleMap);
  });
  sortFilesystemChildren(folder);
  return folder.children;
}

function serviceToFS(serviceData) {
  var root = { name: "root", type: "folder", children: [] };

  var attachmentPaths =
    serviceData && serviceData.attachmentPaths
      ? serviceData.attachmentPaths
      : (serviceData && serviceData.paths) || [];

  if (attachmentPaths && attachmentPaths.length > 0) {
    var pathNodes = buildPathsTree(attachmentPaths);
    root.children.push(...pathNodes);
  }

  return root;
}

export default function FileManagerISF(props) {
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const { t } = useTranslation();
  const [, , isAuthorized] = useAuthorization();
  const {
    handleCategoryCreated,
    onPathChange,
    isEditingSubtitles,
    modifiedSubtitles = {},
    setModifiedSubtitles,
  } = props;

  var normalized = useMemo(
    function () {
      if (props.serviceData) return props.serviceData;
      return {
        startupAttachments: props.startupAttachments || [],
        hospitalAttachments: props.hospitalAttachments || [],
        regulatoryAttachments: props.regulatoryAttachments || [],
        protocolAttachments: props.protocolAttachments || [],
        practitioners: props.practitioners || [],
        attachmentPaths: props.attachmentPaths || props.paths || [],
      };
    },
    [
      props.serviceData,
      props.startupAttachments,
      props.hospitalAttachments,
      props.regulatoryAttachments,
      props.protocolAttachments,
      props.practitioners,
      props.attachmentPaths,
      props.paths,
    ]
  );

  var fsRoot = useMemo(
    function () {
      return serviceToFS(normalized);
    },
    [normalized]
  );

  var [path, setPath] = useState([]);
  var prevServiceDataRef = useRef(undefined);

  function folderPathExists(rootNode, pathArr) {
    var node = rootNode;
    for (var i = 0; i < pathArr.length; i++) {
      var seg = pathArr[i];
      if (!node || !node.children) return false;
      var next = node.children.find(
        (cand) => cand && cand.type === "folder" && cand.name === seg
      );
      if (!next) return false;
      node = next;
    }
    return true;
  }

  useEffect(() => {
    if (
      prevServiceDataRef.current !== undefined &&
      prevServiceDataRef.current !== props.serviceData
    ) {
      if (path.length > 0 && !folderPathExists(fsRoot, path)) {
        setPath([]);
      }
    }
    prevServiceDataRef.current = props.serviceData;
  }, [props.serviceData, fsRoot, path]);

  useEffect(() => {
    onPathChange && onPathChange(path);
  }, [path, onPathChange]);

  var currentNode = useMemo(
    function () {
      return getNodeByPath(fsRoot, path);
    },
    [fsRoot, path]
  );
  var items = currentNode && currentNode.children ? currentNode.children : [];

  const pathToSubtitleMap = useMemo(
    () => buildPathToSubtitleMap(normalized?.attachmentPaths || []),
    [normalized]
  );

  useEffect(() => {
    if (isEditingSubtitles && items && setModifiedSubtitles) {
      setModifiedSubtitles((prev) => {
        const next = { ...prev };
        let changed = false;
        items.forEach((item) => {
          if (isFolder(item)) {
            const folderPath =
              path.length > 0 ? path.join("/") + "/" + item.name : item.name;
            if (next[folderPath] === undefined) {
              next[folderPath] = pathToSubtitleMap[folderPath] || "";
              changed = true;
            }
          }
        });
        return changed ? next : prev;
      });
    }
  }, [isEditingSubtitles, path, items, pathToSubtitleMap, setModifiedSubtitles]);

  const handleSubtitleChange = (folderPath, value) => {
    setModifiedSubtitles &&
      setModifiedSubtitles((prev) => ({ ...prev, [folderPath]: value }));
  };

  var shellStyle = useMemo(function () {
    return {
      background: COLORS.card,
      border: "1px solid " + COLORS.border,
      borderRadius: 12,
      boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
      padding: 12,
      fontFamily: COLORS.font,
    };
  }, []);

  const hasFiles = items.some((it) => !isFolder(it));
  const showCreateCategoryButton = hasFiles || items.length === 0;

  const isInsideFolder = path.length > 0;
  const columns = isInsideFolder ? 4 : 5;
  const gap = 12;
  const gapsTotal = (columns - 1) * gap;
  const colWidth = `calc((100% - ${gapsTotal}px) / ${columns})`;

  return (
    <div style={shellStyle}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        {path.length > 0 && (
          <button
            type="button"
            onClick={() => setPath(path.slice(0, -1))}
            title="Volver"
            style={{
              background: "none",
              border: "1px solid " + COLORS.border,
              borderRadius: 6,
              padding: "3px 8px",
              cursor: "pointer",
              color: COLORS.secondary,
              display: "inline-flex",
              alignItems: "center",
              lineHeight: 1,
            }}
          >
            <FaArrowLeft size={12} />
          </button>
        )}
        <span
          style={{
            cursor: "pointer",
            padding: "3px 6px",
            borderRadius: 6,
            background: path.length === 0 ? "#e8eef8" : "transparent",
            color: COLORS.secondary,
          }}
          onClick={function () {
            setPath([]);
          }}
        >
          {t("clinicalTrials.isfFiles.rootLabel")}
        </span>
        {path.map(function (seg, i) {
          var isLast = i === path.length - 1;
          return (
            <span
              key={seg + "_" + i}
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <span style={{ opacity: 0.6, padding: "0 4px" }}>/</span>
              <span
                style={{
                  cursor: "pointer",
                  padding: "3px 6px",
                  borderRadius: 6,
                  background: isLast ? "#e8eef8" : "transparent",
                  color: COLORS.secondary,
                }}
                onClick={function () {
                  setPath(path.slice(0, i + 1));
                }}
              >
                {seg}
              </span>
            </span>
          );
        })}
      </div>
      {showCreateCategoryButton && !isEditingSubtitles && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowNewCategoryForm(true)}
            disabled={!isAuthorized(["Startups.Edit"])}
          >
            <Add />
            {t("clinicalTrials.isfFiles.createCategory")}
          </button>
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          justifyContent: "flex-start",
          alignItems: "stretch",
        }}
      >
        {items.map(function (item) {
          const itemStyle = {
            flex: `0 0 ${colWidth}`,
            maxWidth: colWidth,
            minWidth: 260,
          };

          var rowKey = isFolder(item)
            ? "folder:" + item.name
            : "file:" + attachmentSlotKeyFromRaw(item.attachment);

          if (isFolder(item)) {
            const folderPath =
              path.length > 0 ? path.join("/") + "/" + item.name : item.name;
            const displaySubtitle = isEditingSubtitles
              ? modifiedSubtitles[folderPath]
              : item.subtitle;
            const showInput = isEditingSubtitles;

            return (
              <div key={rowKey} style={itemStyle}>
                <div
                  style={{
                    border: "1px solid " + COLORS.border,
                    borderRadius: 12,
                    background: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: showInput ? "default" : "pointer",
                    textAlign: "center",
                    width: "100%",
                    height: "100%",
                    padding: 12,
                  }}
                  title={showInput ? undefined : "Abrir carpeta " + item.name}
                  onDoubleClick={
                    showInput
                      ? undefined
                      : function () {
                          setPath(path.concat([item.name]));
                        }
                  }
                >
                  <div style={{ fontSize: 36 }}>
                    <FaFolder color={COLORS.secondary} />
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      color: COLORS.text,
                      fontSize: 12,
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      marginBottom: displaySubtitle || showInput ? "2px" : "5px",
                    }}
                  >
                    {item.name}
                  </div>
                  {showInput ? (
                    <FormControl
                      type="text"
                      size="sm"
                      placeholder={
                        t(
                          "clinicalTrials.isfFiles.subtitleModal.subtitlePlaceholder"
                        ) || "Subtítulo..."
                      }
                      value={modifiedSubtitles[folderPath] ?? ""}
                      onChange={(e) =>
                        handleSubtitleChange(folderPath, e.target.value)
                      }
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: "100%",
                        marginTop: 4,
                        fontSize: 11,
                      }}
                    />
                  ) : (
                    displaySubtitle && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#6c757d",
                          lineHeight: 1.2,
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {displaySubtitle}
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          }

          var etId =
            item.attachment?.entityTypeId ?? item.attachment?.EntityTypeId;
          var resolvedEntityId =
            etId === attachmentEntityType.Hospital
              ? String(item.attachment?.entityId ?? "global")
              : String(item.attachment?.entityId ?? props.entityId ?? "0");
          var practitionerLabel =
            item.attachment?.practitionerName ??
            item.attachment?.PractitionerName;

          return (
            <div key={rowKey} style={itemStyle}>
              <div style={{ borderRadius: 12, width: "100%" }}>
                <DocumentUploadCard
                  attachmentEntityTypeId={item.attachment?.entityTypeId}
                  entityId={resolvedEntityId}
                  protocolId={props.protocolId}
                  attachment={item.attachment}
                  legend={props.legend || "Subir documento"}
                  showDownloadButton={false}
                  editCategoryName={false}
                  onCategoryNameChange={function () {}}
                  onAttachmentChange={(attachment) =>
                    props.onAttachmentChanged(
                      item.attachment?.entityTypeId ?? attachment.entityTypeId,
                      resolvedEntityId,
                      attachment
                    )
                  }
                  disabled={!isAuthorized(["Startups.Edit"])}
                />
                {etId === attachmentEntityType.Practitioner &&
                  !!practitionerLabel && (
                    <p
                      style={{
                        marginTop: 8,
                        marginBottom: 0,
                        fontSize: 13,
                      }}
                    >
                      {t(
                        "regulatorySubmissions.item.attachmentPractitionerLabel"
                      )}{" "}
                      {practitionerLabel}
                    </p>
                  )}
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <div
            style={{
              padding: 16,
              border: "1px dashed " + COLORS.border,
              borderRadius: 12,
              textAlign: "center",
              color: "#6c757d",
              width: "100%",
            }}
          >
            {t("clinicalTrials.isfFiles.emptyFolderMessage")}
          </div>
        )}
      </div>
      <ModalCreateCategory
        show={showNewCategoryForm}
        onClose={() => setShowNewCategoryForm(false)}
        onCreated={handleCategoryCreated}
        currentPath={path}
      />
    </div>
  );
}
